import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import { DocumentGenerationForm } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/DocumentGenerationForm.tsx';
import { CertificateGenerationDocumentId } from '~/store/covenants/types.ts';
import { BorrowerType } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TFinancialConditionCertificateResponse,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';

interface IDocumentGenerationModalProps {
  open: boolean;
  documentId: string | number;
  documentName: string;
  isGenerating: boolean;
  onSaveFinancialCondition: (values: TFinancialConditionFormValues) => Promise<boolean>;
  onLoadFinancialCondition: () => Promise<TFinancialConditionCertificateResponse | null>;
  onSaveIncomeStructureByKved: (values: TIncomeStructureByKvedFormValues) => Promise<boolean>;
  onLoadIncomeStructureByKved: () => Promise<TIncomeStructureByKvedCertificateResponse | null>;
  onGenerate: (isFop?: boolean) => void;
  onClose: () => void;
}

export const DocumentGenerationModal: React.FC<IDocumentGenerationModalProps> = ({
  open,
  documentId,
  documentName,
  isGenerating,
  onSaveFinancialCondition,
  onLoadFinancialCondition,
  onSaveIncomeStructureByKved,
  onLoadIncomeStructureByKved,
  onGenerate,
  onClose,
}) => {
  const isFinancialCondition =
    String(documentId) === CertificateGenerationDocumentId.FinancialCondition;
  const isIncomeStructureByKved =
    String(documentId) === CertificateGenerationDocumentId.IncomeStructureByKved;
  const isSaveableCertificate = isFinancialCondition || isIncomeStructureByKved;
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [borrowerType, setBorrowerType] = useState<BorrowerType | null>(null);
  const isBusy = isGenerating || isSaving;
  const formId = `document-generation-form-${documentId}`;

  useEffect(() => {
    if (!open) {
      setIsSaved(false);
      setBorrowerType(null);
    }
  }, [open]);

  const handleSave = async (values: TFinancialConditionFormValues): Promise<boolean> => {
    setIsSaving(true);

    try {
      const isSaveSuccessful = await onSaveFinancialCondition(values);

      if (isSaveSuccessful) {
        setIsSaved(true);
      }

      return isSaveSuccessful;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveIncomeStructureByKved = async (
    values: TIncomeStructureByKvedFormValues
  ): Promise<boolean> => {
    setIsSaving(true);

    try {
      const isSaveSuccessful = await onSaveIncomeStructureByKved(values);

      if (isSaveSuccessful) {
        setIsSaved(true);
      }

      return isSaveSuccessful;
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormChange = useCallback(() => {
    setIsSaved(false);
  }, []);

  const handleBorrowerTypeChange = (nextBorrowerType: BorrowerType | null) => {
    setBorrowerType(nextBorrowerType);
  };

  const isFop = borrowerType === BorrowerType.SoleTrader;

  return (
    <Dialog
    open={open}
    onClose={isBusy ? undefined : onClose}
    maxWidth={false}
    slotProps={{ paper: { sx: appFormStyles.documentGenerationDialogPaper } }}
  >
    <DialogTitle sx={appFormStyles.documentGenerationDialogTitle}>
      <Typography component="span" sx={appFormStyles.documentGenerationDialogTitleText}>
        Генерація «{documentName}»
      </Typography>

      <IconButton
        aria-label="Закрити"
        onClick={onClose}
        disabled={isBusy}
        sx={appFormStyles.documentGenerationDialogCloseButton}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>

    <DialogContent sx={appFormStyles.documentGenerationDialogContent}>
      {open && (
        <DocumentGenerationForm
          documentId={documentId}
          formId={formId}
          disabled={isBusy}
          onSaveFinancialCondition={isFinancialCondition ? handleSave : undefined}
          onLoadFinancialCondition={isFinancialCondition ? onLoadFinancialCondition : undefined}
          onSaveIncomeStructureByKved={
            isIncomeStructureByKved ? handleSaveIncomeStructureByKved : undefined
          }
          onLoadIncomeStructureByKved={
            isIncomeStructureByKved ? onLoadIncomeStructureByKved : undefined
          }
          onDirtyChange={isSaveableCertificate ? handleFormChange : undefined}
          onBorrowerTypeChange={isFinancialCondition ? handleBorrowerTypeChange : undefined}
        />
      )}
    </DialogContent>

    <DialogActions sx={appFormStyles.documentGenerationDialogActions}>
      {isSaveableCertificate && (
        <Button variant="outlined" type="submit" form={formId} disabled={isBusy}>
          {isSaving ? <CircularProgress size={18} color="inherit" /> : 'Зберегти'}
        </Button>
      )}
      <Button
        variant="contained"
        onClick={() => onGenerate(isFinancialCondition ? isFop : undefined)}
        disabled={
          isBusy ||
          (isSaveableCertificate && !isSaved) ||
          (isFinancialCondition && !borrowerType)
        }
      >
        {isGenerating ? <CircularProgress size={18} color="inherit" /> : 'Згенерувати документ'}
      </Button>
    </DialogActions>
    </Dialog>
  );
};
